/**
 * Search Page
 *
 * Displays search results with filters and infinite scroll.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router'
import { PostCard } from '@/components/feed/PostCard'
import { PostSkeleton } from '@/components/feed/PostSkeleton'
import { SearchFilters } from '@/components/search/SearchFilters'
import { SearchBar } from '@/components/search/SearchBar'
import { Button } from '@/components/ui/button'
import { useSearch, getAllSearchPosts } from '@/hooks/useSearch'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { UserMenu } from '@/components/user/UserMenu'
import { NotificationMenu } from '@/components/notifications'
import { useAuthStore } from '@/stores/authStore'
import { RefreshCw, ArrowLeft, Search } from 'lucide-react'
import type { SearchField, SearchSortField, SearchSortOrder } from '@/services/steemworld'

export default function SearchPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)

  // Parse search params
  const query = searchParams.get('q') || ''
  const author = searchParams.get('author') || undefined
  const tags = searchParams.get('tags') || undefined
  const searchIn = (searchParams.get('in') as SearchField) || 'title,body'
  const sortBy = (searchParams.get('sort') as SearchSortField) || 'created'
  const order = (searchParams.get('order') as SearchSortOrder) || 'desc'

  const filters = useMemo(
    () => ({ author, tags, searchIn, sortBy, order }),
    [author, tags, searchIn, sortBy, order]
  )

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSearch({
    query,
    ...filters,
    enabled: query.length > 0,
  })

  const loadMoreRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const posts = getAllSearchPosts(data)

  const handleSearch = useCallback(
    (newQuery: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('q', newQuery)
      navigate(`/search?${params.toString()}`)
    },
    [navigate, searchParams]
  )

  const handleFilterChange = useCallback(
    (newFilters: {
      author?: string
      tags?: string
      searchIn?: SearchField
      sortBy?: SearchSortField
      order?: SearchSortOrder
    }) => {
      const params = new URLSearchParams()
      params.set('q', query)

      if (newFilters.author) params.set('author', newFilters.author)
      if (newFilters.tags) params.set('tags', newFilters.tags)
      if (newFilters.searchIn && newFilters.searchIn !== 'title,body')
        params.set('in', newFilters.searchIn)
      if (newFilters.sortBy && newFilters.sortBy !== 'created')
        params.set('sort', newFilters.sortBy)
      if (newFilters.order && newFilters.order !== 'desc')
        params.set('order', newFilters.order)

      navigate(`/search?${params.toString()}`)
    },
    [navigate, query]
  )

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center gap-4 px-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>

          <div className="flex-1 max-w-xl">
            <SearchBar
              value={query}
              onSearch={handleSearch}
              placeholder="Search posts..."
              autoFocus
            />
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <>
                <NotificationMenu />
                <UserMenu />
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container max-w-2xl px-4 py-6">
        {/* Filters */}
        <div className="mb-6">
          <SearchFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Results Header */}
        {query && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              {isLoading
                ? 'Searching...'
                : `Found ${posts.length}${hasNextPage ? '+' : ''} results for "${query}"`}
            </p>
          </div>
        )}

        {/* No query state */}
        {!query && (
          <div className="rounded-lg border bg-card p-12 text-center">
            <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              Enter a search term to find posts
            </p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && query && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <PostSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
            <p className="text-destructive mb-4">
              {error instanceof Error ? error.message : 'Search failed'}
            </p>
            <Button variant="outline" onClick={() => refetch()} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          </div>
        )}

        {/* Empty results */}
        {!isLoading && !isError && query && posts.length === 0 && (
          <div className="rounded-lg border bg-card p-12 text-center">
            <p className="text-lg text-muted-foreground">
              No posts found for "{query}"
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Try different keywords or remove some filters
            </p>
          </div>
        )}

        {/* Results */}
        {!isLoading && posts.length > 0 && (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard key={`${post.author}-${post.permlink}`} post={post} />
            ))}

            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="py-4">
              {isFetchingNextPage && <PostSkeleton />}
              {hasNextPage && !isFetchingNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="ghost"
                    onClick={() => fetchNextPage()}
                    className="gap-2"
                  >
                    Load More
                  </Button>
                </div>
              )}
              {!hasNextPage && posts.length > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  No more results
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
